import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');
const SITE_USER_STORAGE_KEY = 'aomori-zaidan-site-user';
const ADMIN_USERS_AUTH_KEY = 'aomori-zaidan-admin-users-auth';

/** 権限スラグ（APIと一致） */
export const PERMISSIONS = {
  user_manage: 'user_manage',
  schedule_manage: 'schedule_manage',
  booking_use: 'booking_use',
  profile_edit: 'profile_edit',
  booking_view: 'booking_view',
} as const;

export type AuthUser = {
  id: string;
  email: string;
  name: string | null;
  company?: string | null;
  contact_name?: string | null;
  department?: string | null;
  role: { id: number; name: string; slug: string } | null;
  permissions: string[];
};

/** ユーザーが指定権限を持つか */
export function can(user: AuthUser | null, permission: string): boolean {
  return Array.isArray(user?.permissions) && user.permissions.includes(permission);
}

type AuthContextValue = {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refetch: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

async function phpFetch(path: string, options: RequestInit = {}) {
  const base = import.meta.env.BASE_URL ?? '/';
  const prefix = API_BASE || `${base.replace(/\/$/, '')}/api`;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  const url = `${prefix}${cleanPath}`;
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    // 1. PHP API による現在のセッション取得
    try {
      const res = await phpFetch(`/me.php?_t=${Date.now()}`, { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        const u = data.user;
        if (u) {
          const authUser: AuthUser = {
            id: String(u.id),
            email: u.email ?? '',
            name: u.name ?? null,
            company: u.company ?? u.name ?? null,
            contact_name: u.contact_name ?? null,
            department: u.department ?? null,
            role: u.role ?? null,
            permissions: Array.isArray(u.permissions) ? u.permissions : [],
          };
          setUser(authUser);
          localStorage.setItem(SITE_USER_STORAGE_KEY, JSON.stringify(authUser));
          setLoading(false);
          return;
        }
      }
    } catch {
      /* ignore */
    }

    // 2. localStorage キャッシュの復元
    const stored = localStorage.getItem(SITE_USER_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed?.email) {
          setUser(parsed);
          setLoading(false);
          return;
        }
      } catch {
        localStorage.removeItem(SITE_USER_STORAGE_KEY);
      }
    }

    setUser(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const login = useCallback(async (email: string, password: string) => {
    // 1. PHP API へのログイン試行
    try {
      const res = await phpFetch('/login.php', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.user) {
        const u = data.user;
        const authUser: AuthUser = {
          id: String(u.id),
          email: u.email ?? '',
          name: u.name ?? null,
          company: u.company ?? u.name ?? null,
          contact_name: u.contact_name ?? null,
          department: u.department ?? null,
          role: u.role ?? null,
          permissions: Array.isArray(u.permissions) ? u.permissions : [],
        };
        setUser(authUser);
        localStorage.setItem(SITE_USER_STORAGE_KEY, JSON.stringify(authUser));
        return;
      }
      if (res.status === 401 || res.status === 400) {
        throw new Error(data.error || 'メールアドレスまたはパスワードが正しくありません。');
      }
    } catch (err) {
      if (err instanceof Error && err.message !== 'Failed to fetch') {
        throw err;
      }
    }

    // 2. 管理画面で追加されたアカウントをローカル照合（オフライン/バックアップ）
    try {
      const authsRaw = localStorage.getItem(ADMIN_USERS_AUTH_KEY);
      if (authsRaw) {
        const auths = JSON.parse(authsRaw);
        if (auths[email] && auths[email].password === password) {
          const authUser: AuthUser = {
            id: auths[email].id,
            email,
            name: null,
            role: {
              id: auths[email].role === 'admin' ? 1 : 2,
              name: auths[email].role === 'admin' ? '管理者' : '企業（ユーザー）',
              slug: auths[email].role,
            },
            permissions: auths[email].role === 'admin'
              ? ['user_manage', 'schedule_manage', 'booking_use', 'profile_edit', 'booking_view']
              : ['booking_use', 'profile_edit', 'booking_view'],
          };
          setUser(authUser);
          localStorage.setItem(SITE_USER_STORAGE_KEY, JSON.stringify(authUser));
          return;
        }
      }
    } catch {
      /* ignore */
    }

    throw new Error('メールアドレスまたはパスワードが正しくありません。');
  }, []);

  const logout = useCallback(async () => {
    try {
      await phpFetch('/logout.php', { method: 'POST' });
    } catch {
      /* ignore */
    }
    localStorage.removeItem(SITE_USER_STORAGE_KEY);
    setUser(null);
  }, []);

  const value: AuthContextValue = { user, loading, login, logout, refetch };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
